import { useMemo } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import I18n from 'I18n';
import { MessageAttachment } from './MessageAttachment';
import { useIsMobile } from '../hooks/useBreakpoint';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { NoAttachments } from './NoAttachments';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useParams } from 'quartz-routing/react-router';
import { useThreadAttachments } from '../data/useThreadAttachments';
import { MAX_ATTACHMENTS_PER_THREAD } from '../constants';
import { CollapsiblePanel } from './CollapsiblePanel';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AttachmentsPanelContent = ({
  attachments,
  isLoading
}) => {
  const loadedMaxAttachments = attachments.length === MAX_ATTACHMENTS_PER_THREAD;
  if (isLoading && attachments.length === 0) {
    return /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "400",
      alignItems: "center",
      justifyContent: "center",
      marginBlockStart: "800",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {})
    });
  }
  return attachments.length > 0 ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [attachments.map(attachment => /*#__PURE__*/_jsx(MessageAttachment, {
      attachment: attachment.attachment,
      sensitiveAttachment: attachment.sensitiveAttachment,
      customSubtitle: attachment.sentDate ? I18n.moment(attachment.sentDate).format('LL') : null
    }, attachment.fileId)), loadedMaxAttachments && /*#__PURE__*/_jsx(Text, {
      as: "p",
      typography: "detail-200",
      textAlign: "center",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.attachmentsPanel.displayingMaxAttachments",
        options: {
          MAX_ATTACHMENTS_PER_THREAD
        }
      })
    })]
  }) : /*#__PURE__*/_jsx(NoAttachments, {});
};
export function AttachmentsPanel({
  showAsOverlay,
  isAttachmentsPanelOpen,
  setIsAttachmentsPanelOpen
}) {
  const isMobile = useIsMobile();
  const {
    ticketId,
    threadId
  } = useParams();
  const {
    data: threadAttachments,
    loading
  } = useThreadAttachments(Number(ticketId), threadId || '');
  const combinedAttachments = useMemo(() => {
    if (!threadAttachments) return [];
    const {
      attachmentsList,
      sensitiveAttachmentsList
    } = threadAttachments;
    return [...attachmentsList.map((attachment, i) => ({
      fileId: `attachment-${i}`,
      sensitiveAttachment: undefined,
      attachment,
      sentDate: attachment.attachmentTimestamp
    })), ...sensitiveAttachmentsList.map((sensitiveAttachment, i) => ({
      fileId: `sensitive-attachment-${i}`,
      sensitiveAttachment,
      attachment: undefined,
      sentDate: undefined
    }))].sort((a, b) => (a.sentDate || Infinity) - (b.sentDate || Infinity));
  }, [threadAttachments]);
  const titleNode = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "customer-portal-app.attachmentsPanel.title"
  });
  if (showAsOverlay) {
    return /*#__PURE__*/_jsx(CollapsiblePanel, {
      mode: "overlay",
      "data-test-id": "attachments-panel",
      isOpen: isAttachmentsPanelOpen,
      onClose: () => setIsAttachmentsPanelOpen(false),
      align: "right",
      inlineSize: isMobile ? '100dvw' : '435px',
      ariaCloseLabel: I18n.text('customer-portal-app.attachmentsPanel.ariaClose'),
      title: titleNode,
      children: /*#__PURE__*/_jsx(AttachmentsPanelContent, {
        attachments: combinedAttachments,
        isLoading: loading
      })
    });
  }
  return /*#__PURE__*/_jsx(CollapsiblePanel, {
    mode: "sidebar",
    "data-test-id": "attachments-panel",
    borderSide: "start",
    iconRotated: true,
    expandAriaLabel: I18n.text('customer-portal-app.attachmentsPanel.expand'),
    collapseAriaLabel: I18n.text('customer-portal-app.attachmentsPanel.collapse'),
    title: titleNode,
    children: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "400",
      flex: "1",
      inlineSize: "100%",
      maxInlineSize: "100%",
      minInlineSize: "0",
      backgroundColor: "fill-surface-default",
      overflow: "auto",
      children: /*#__PURE__*/_jsx(AttachmentsPanelContent, {
        attachments: combinedAttachments,
        isLoading: loading
      })
    })
  });
}